import { useState } from 'react'
import { useTasks } from '../hooks/useTasks'
import { Button } from '../../../components/ui/Button'
import { Spinner } from '../../../components/ui/Spinner'
import { ConflictModal } from '../../../components/ConflictModal'
import { supabase } from '../../../lib/supabase'
import { Portal } from '../../../components/Portal'

const RECUR_OPTS = [
  { value: '',         label: 'Does not repeat' },
  { value: 'FREQ=DAILY;INTERVAL=1',   label: 'Daily' },
  { value: 'FREQ=WEEKLY;INTERVAL=1',  label: 'Weekly' },
  { value: 'FREQ=MONTHLY;INTERVAL=1', label: 'Monthly' },
]

export const TaskForm = ({ task, members, events = [], onClose }) => {
  const { createTask, updateTask } = useTasks()
  const editing = !!task

  const [title,       setTitle]       = useState(task?.title       ?? '')
  const [description, setDescription] = useState(task?.description ?? '')
  const [dueDate,     setDueDate]     = useState(task?.due_date?.slice(0, 10) ?? '')
  const [assignedTo,  setAssignedTo]  = useState(task?.assigned_to ?? '')
  const [recurrence,  setRecurrence]  = useState(task?.recurrence_rule ?? '')
  const [eventId,     setEventId]     = useState(task?.event_id ?? '')
  const [saving,      setSaving]      = useState(false)
  const [error,       setError]       = useState('')
  const [conflict,    setConflict]    = useState(null)
  const [pendingPayload, setPendingPayload] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!title.trim()) { setError('Title is required'); return }
    setSaving(true)
    setError('')
    try {
      const payload = {
        title:           title.trim(),
        description:     description || null,
        due_date:        dueDate ? new Date(dueDate).toISOString() : null,
        assigned_to:     assignedTo || null,
        recurrence_rule: recurrence || null,
        event_id:        eventId || null,
      }
      if (editing) {
        const result = await updateTask({ id: task.id, localUpdatedAt: task.updated_at, ...payload })
        if (result?.conflict) {
          setPendingPayload(payload)
          setConflict(result.conflict)
          setSaving(false)
          return
        }
      } else {
        await createTask(payload)
      }
      onClose()
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const handleKeepMine = async () => {
    setSaving(true)
    setConflict(null)
    try {
      const { error: err } = await supabase.from('tasks').update(pendingPayload).eq('id', task.id)
      if (err) throw err
      onClose()
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <form
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
          <h2 className="text-base font-bold text-text-primary">{editing ? 'Edit Task' : 'New Task'}</h2>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-4">
          <input
            autoFocus
            type="text"
            placeholder="Task title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />

          <textarea
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
          />

          <div>
            <label className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1 block">Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1 block">Assign To</label>
            <select
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Unassigned</option>
              {(members ?? []).filter((m) => m.profiles).map((m) => (
                <option key={m.profiles.id} value={m.profiles.id}>{m.profiles.display_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1 block">Repeat</label>
            <select
              value={recurrence}
              onChange={(e) => setRecurrence(e.target.value)}
              className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {RECUR_OPTS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {events.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1 block">Link to Event (optional)</label>
              <select
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
                className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">No event</option>
                {events.map((e) => (
                  <option key={e.id} value={e.id}>{e.title}</option>
                ))}
              </select>
            </div>
          )}

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>

        <div className="px-5 py-4 border-t border-border flex gap-2 flex-shrink-0">
          <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" className="flex-1" disabled={saving}>
            {saving ? <Spinner size="sm" /> : editing ? 'Save' : 'Create Task'}
          </Button>
        </div>
      </form>

      {conflict && (
        <ConflictModal
          localRecord={pendingPayload}
          serverRecord={conflict.serverRecord}
          onKeepMine={handleKeepMine}
          onUseServer={() => { setConflict(null); onClose() }}
        />
      )}
    </div>
    </Portal>
  )
}
